import React, { useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { goalTypeToUrl } from '../utils/goalTypeUtils';
import { getGoalDisplayTitle, getGoalIconUrl, onGoalIconError } from '../utils/goalDisplayTitle';
import { getStatusCircleClass } from '../utils/euStatusUtils';
import { getTrendArrow } from '../utils/euTrendUtils';
import HomeHero from './HomeHero';
import FrameworkSwitcher from './FrameworkSwitcher';
import HomeExplorePanel from './HomeExplorePanel';
import ProfileDownloadBar from './ProfileDownloadBar';
import HomeIndicatorsList from './HomeIndicatorsList';
import '../utils/sharedStyles.css';

const GoalGridMobile = ({
  goals,
  goalType,
  indicators,
  indicatorsLoading,
  title,
  subtitle,
  reportDownloadTitle,
  homeCopy = {},
  handleGoalClick,
  handleIndicatorClick,
}) => {
  const clickTimeoutRef = useRef({});
  const isNavigatingRef = useRef(false);

  const copy = {
    legendHeading: goalType !== 'un' ? 'SDG Progress & Status Assessment' : 'SDG Progress',
    statusAssessmentBlurb:
      'STATUS: Where Kosovo sits among EU-27 comparison countries (benchmark quintiles)',
    progressBlurb: 'PROGRESS: Direction and pace of progress toward each SDG target',
    goalCardIndicatorsTitle: 'View All Indicators',
    goalCardIndicatorsSubtitle: 'View all indicators for this goal',
    goalCardTargetsTitle: 'Indicators grouped by Targets',
    indicatorsSectionTitle: goalType === 'eu' ? 'EU SDG Indicators' : 'UN SDG Indicators',
    indicatorsSectionSubtitle: 'Click the indicator to view the latest data',
    viewAllIndicatorsLink: 'View all indicators (including those without data yet)',
    noIndicatorsMessage: `No indicators available for ${goalType === 'eu' ? 'EU' : 'UN'} SDGs`,
    reportDownloadLabel: reportDownloadTitle,
    ...homeCopy,
  };

  const downloadLabel =
    copy.reportDownloadLabel ||
    reportDownloadTitle ||
    (goalType === 'eu'
      ? 'Download Kosovo EU SDG Profile (PDF)'
      : 'Download Kosovo SDG Profile (PDF)');

  const handleLinkClick = useCallback(
    (e, goal) => {
      const goalId = goal.id;
      const now = Date.now();
      const lastClick = clickTimeoutRef.current[goalId] || 0;

      if (now - lastClick < 500 || isNavigatingRef.current) {
        e.preventDefault();
        return;
      }

      clickTimeoutRef.current[goalId] = now;
      isNavigatingRef.current = true;
      handleGoalClick(goal);

      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 1000);
    },
    [handleGoalClick]
  );

  return (
    <div className="mobile-only home-redesign">
      <HomeHero title={title} subtitle={subtitle} />
      <div className="mobile-wrapper">
        <FrameworkSwitcher copy={copy} />

        <HomeExplorePanel goalType={goalType} copy={copy}>
          <div className="mobile-goals">
            {goals.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center' }}>No goals found</div>
            )}
            {goals.map((goal) => (
              <div key={goal.id} className="mobile-goal-card">
                <div className="mobile-goal-head">
                  <div className="mobile-goal-icon-container">
                    <img
                      className="mobile-goal-icon"
                      src={getGoalIconUrl(goal)}
                      alt={getGoalDisplayTitle(goal)}
                      onError={(e) => onGoalIconError(e, goal)}
                    />
                  </div>
                  <div className="mobile-statuses" aria-label="Goal assessment">
                    {goalType !== 'un' && (
                      <div className="mobile-status-item">
                        <span className="mobile-status-item__label">Status</span>
                        {goal?.computed?.goal_semaphore_status ? (
                          <div
                            className={`status-circle status-circle-medium ${getStatusCircleClass(
                              goal.computed.goal_semaphore_status
                            )}`}
                            title={goal.computed.goal_semaphore_status}
                          />
                        ) : (
                          <span className="mobile-status-item__empty">—</span>
                        )}
                      </div>
                    )}
                    <div className="mobile-status-item">
                      <span className="mobile-status-item__label">Progress</span>
                      {goal?.computed?.goal_trend_status ? (
                        (() => {
                          const { arrow, className } = getTrendArrow(goal.computed.goal_trend_status);
                          return (
                            <span
                              className={`trend-arrow trend-arrow-medium ${className}`}
                              title={goal.computed.goal_trend_status}
                            >
                              {arrow}
                            </span>
                          );
                        })()
                      ) : (
                        <span className="trend-arrow trend-arrow-medium trend-arrow-default">—</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mobile-goal-info">
                  <div className="mobile-goal-name">{getGoalDisplayTitle(goal)}</div>
                </div>
                <div className="mobile-goal-actions">
                  {goalType === 'un' && (
                    <Link
                      to={`/${goalTypeToUrl(goalType)}/goal/${goal.id}/targets`}
                      onClick={(e) => handleLinkClick(e, goal)}
                      className="mobile-action-link"
                    >
                      <div className="mobile-action-btn">
                        <div className="mobile-action-text">
                          <h4>{copy.goalCardTargetsTitle}</h4>
                        </div>
                        <span className="chevron">→</span>
                      </div>
                    </Link>
                  )}
                  <Link
                    to={`/${goalTypeToUrl(goalType)}/goal/${goal.id}/indicators`}
                    onClick={(e) => handleLinkClick(e, goal)}
                    className="mobile-action-link"
                  >
                    <div className="mobile-action-btn">
                      <div className="mobile-action-text">
                        <h4>{copy.goalCardIndicatorsTitle}</h4>
                        <p>{copy.goalCardIndicatorsSubtitle}</p>
                      </div>
                      <span className="chevron">→</span>
                    </div>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </HomeExplorePanel>

        <ProfileDownloadBar
          to={`/${goalTypeToUrl(goalType)}/sdg-profile`}
          label={downloadLabel}
        />

        <hr className="mobile-divider" />

        <HomeIndicatorsList
          goals={goals}
          goalType={goalType}
          indicators={indicators}
          indicatorsLoading={indicatorsLoading}
          copy={copy}
          handleIndicatorClick={handleIndicatorClick}
          variant="mobile"
        />
      </div>
    </div>
  );
};

export default GoalGridMobile;
